import React, {useCallback, useContext, useEffect, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory} from "react-router-dom";
import {EntityAccountsContext} from "components/entity-accounts/context";
import {WireTemplatesContext} from "./context";
import {DEFAULT_SETTLEMENT_CURRENCY} from "./context/WireTemplatesContext";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import {
    WIRE_TEMPLATE_NAME_INVALID_LENGTH_ERR_MSG,
    WIRE_TEMPLATE_PURPOSE_CODE_INVALID_LENGTH_ERR_MSG
} from "utils/ConstantsMsg";
import {CustomSelect} from "../../../components/CustomSelect";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";

export const WIRE_TEMPLATE_STEP_URL = '/admin/wire-templates-new-step';
export const isValidWireTemplateStep = (step) => ['1', '2', '3', '4', '5', '6'].includes(step?.toString());
export const getWireTemplateStepUrl = (step) => WIRE_TEMPLATE_STEP_URL + step;
export const shouldNavigateToWireTemplateStep = (currentStep, newPageStep) => {
    const step = newPageStep?.toString();
    return isValidWireTemplateStep(step) && step !== currentStep;
}

export const USABA_CLEARING_SYSTEM_CODE = 'USABA';
export const USABA_CLEARING_SYSTEM_ID_PREFIX = 'FW';

export const getClearingSystemIdWithPrefix = (value) => {
    if (!value) {
        return value;
    }

    const clearingSystemId = value.toString();
    const clearingSystemIdUpperCase = clearingSystemId.toUpperCase();

    if (USABA_CLEARING_SYSTEM_ID_PREFIX.startsWith(clearingSystemIdUpperCase) && clearingSystemIdUpperCase.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        return clearingSystemIdUpperCase;
    }

    return clearingSystemIdUpperCase.startsWith(USABA_CLEARING_SYSTEM_ID_PREFIX)
        ? `${USABA_CLEARING_SYSTEM_ID_PREFIX}${clearingSystemId.slice(USABA_CLEARING_SYSTEM_ID_PREFIX.length)}`
        : `${USABA_CLEARING_SYSTEM_ID_PREFIX}${clearingSystemId}`;
}

export const TEMPLATE_NAME_ACCOUNT_PART_LENGTH = 5;
export const TEMPLATE_NAME_CREDITOR_AGENT_PART_LENGTH = 10;

const getAccountNameFragmentForTemplate = (accountDisplayName) => {
    if (!accountDisplayName) {
        return '';
    }

    const displayNameStr = accountDisplayName.toString();
    const dashIndex = displayNameStr.indexOf('-');
    const nameAfterDash = dashIndex >= 0 ? displayNameStr.slice(dashIndex + 1) : displayNameStr;

    return nameAfterDash.trim().slice(0, TEMPLATE_NAME_ACCOUNT_PART_LENGTH).toUpperCase();
}

// Default Template Name = First 5 letters of account display name (letters after '-') + Settlement Currency + First 10 letters of Creditor Agent Name
// e.g. GLAZE_USD_BARCLAY
export const generateDefaultWireTemplateName = ({accountDisplayName, settlementCurrency, creditorAgentName} = {}) => {
    const accountPart = getAccountNameFragmentForTemplate(accountDisplayName);
    const currencyPart = (settlementCurrency || '').toString().trim().toUpperCase();
    const creditorAgentPart = (creditorAgentName || '').toString().trim().slice(0, TEMPLATE_NAME_CREDITOR_AGENT_PART_LENGTH).toUpperCase();

    return [accountPart, currencyPart, creditorAgentPart].filter(Boolean).join('_');
}

export const REMITTANCE_INFO_MAX_LINES = 4;
export const REMITTANCE_INFO_MAX_LINE_LENGTH = 35;
export const REMITTANCE_INFO_TOO_MANY_LINES_MSG = `Remittance Information cannot exceed ${REMITTANCE_INFO_MAX_LINES} lines`;
export const REMITTANCE_INFO_LINE_TOO_LONG_MSG = `Each line of Remittance Information cannot exceed ${REMITTANCE_INFO_MAX_LINE_LENGTH} characters`;
export const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 \r\n]+$/
export const ALPHANUMERIC_REGEX_INVALID_MSG = `Prohibited character, only alphanumeric characters are allowed`;

export const validateRemittanceInformation = (remittanceInformation) => {
    if (!remittanceInformation) {
        return '';
    }
    const lines = remittanceInformation.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    if (lines.length > REMITTANCE_INFO_MAX_LINES) {
        return REMITTANCE_INFO_TOO_MANY_LINES_MSG;
    }
    if (lines.some(line => line.length > REMITTANCE_INFO_MAX_LINE_LENGTH)) {
        return REMITTANCE_INFO_LINE_TOO_LONG_MSG;
    }
    if (!ALPHANUMERIC_REGEX.test(remittanceInformation)) {
        return ALPHANUMERIC_REGEX_INVALID_MSG;
    }
    return '';
}

export const validateStep1 = (addWireTemplateState) => {
    let errors = {}
    const {managerId, accountId, templateName, remittanceInformation, purposeCode, settlementCurrency} = addWireTemplateState;
    if (!managerId || managerId === '' || managerId === -1) {
        errors['managerId'] = 'Please select entity'
    }

    if (!accountId || accountId === '') {
        errors['accountId'] = 'Please select account'
    }

    if (!templateName || templateName.trim() === '') {
        // no empty check here, will check on last page.
    } else if (templateName.trim().length > 30) {
        errors['templateName'] = WIRE_TEMPLATE_NAME_INVALID_LENGTH_ERR_MSG
    }

    if (purposeCode && purposeCode.trim().length > 50) {
        errors['purposeCode'] = WIRE_TEMPLATE_PURPOSE_CODE_INVALID_LENGTH_ERR_MSG
    }

    if (!settlementCurrency) {
        errors['settlementCurrency'] = 'Please select settlement currency'
    }

    const remittanceError = validateRemittanceInformation(remittanceInformation);
    if (remittanceError) {
        errors['remittanceInformation'] = remittanceError;
    }

    return errors
}

export default function AddNewWireTemplateStep1() {

    const history = useHistory();
    const {entityAccountsOptionsNoSwap, accountsOptionsNoSwap, setManagerDetails, setAccountDetails, clearEntityAccounts} = useContext(EntityAccountsContext);
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState} = useContext(WireTemplatesContext);
    const {makeGetRequest} = useContext(SecurityContext);
    const [options, setOptions] = useState([]);
    const [localEntityAccountsOptions, setLocalEntityAccountsOptions] = useState(JSON.parse(JSON.stringify(entityAccountsOptionsNoSwap)));
    const [selectedOption, setSelectedOption] = useState({});
    const [currencies, setCurrencies] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    const currentStepPage = '1'
    const backStepPage = '1'
    const nextStepPage = '2'

    const getSelectedManagerName = useCallback(() => {
        if (addWireTemplateState.managerName) {
            return addWireTemplateState.managerName;
        }

        const selectedEntity = entityAccountsOptionsNoSwap.find(entity => entity.managerId?.toString() === addWireTemplateState.managerId?.toString());

        return selectedEntity ? `${selectedEntity.shortName}-${selectedEntity.longName}` : '';
    }, [addWireTemplateState.managerId, addWireTemplateState.managerName, entityAccountsOptionsNoSwap])

    const getCurrencies = useCallback(() => {
        const manager = getSelectedManagerName();

        if (!manager) {
            setCurrencies([]);
            return;
        }

        const onSuccessResponse = (res) => {
            const supportedCurrencies = res.data || [];
            setCurrencies(supportedCurrencies);
            setAddWireTemplateState(previousState => {
                if (!previousState.settlementCurrency) {
                    return previousState;
                }

                const isSelectedCurrencySupported = supportedCurrencies.some(currency => {
                    const currencyCode = currency.currencyCode || currency.isoCurrencyCode;
                    return currencyCode?.toString() === previousState.settlementCurrency?.toString();
                });

                return isSelectedCurrencySupported
                    ? previousState
                    : {...previousState, settlementCurrency: ''};
            });
        }

        const onErrorResponse = () => {}

        makeGetRequest(URL.WIRES_CURRENCY_URL, {manager}, onSuccessResponse, onErrorResponse)
    }, [getSelectedManagerName, makeGetRequest, setAddWireTemplateState])

    useEffect(() => {
        getCurrencies()
    }, [getCurrencies])

    useEffect(() => {
        if (shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history])

    useEffect(() => {
        setLocalEntityAccountsOptions(JSON.parse(JSON.stringify(entityAccountsOptionsNoSwap)))
    }, [entityAccountsOptionsNoSwap])

    useEffect(() => {
        const entityOptions = []
        localEntityAccountsOptions.forEach(e => {
            let option = {}
            option['value'] = e.managerId
            option['label'] = e.shortName + '-' + e.longName
            entityOptions.push(option)
        })
        setOptions(entityOptions)
    }, [localEntityAccountsOptions])

    useEffect(() => {
        const selectedEntity = options.filter(e => e.value.toString() === addWireTemplateState.managerId?.toString())
        if (selectedEntity.length > 0) {
            setSelectedOption(selectedEntity[0]);
        } else {
            setSelectedOption({})
        }
    }, [options, addWireTemplateState.managerId])

    const updateFormError = (fieldName) => {
        if (formErrors[fieldName]) {
            setFormErrors(previousErrors => ({...previousErrors, [fieldName]: ''}))
        }
    }

    const handleChange = (e) => {
        const {name, value} = e.currentTarget;
        const newAddWireTemplateState = {...addWireTemplateState, [name]: value};

        if (name === 'settlementCurrency') {
            newAddWireTemplateState.templateName = generateDefaultWireTemplateName({
                accountDisplayName: addWireTemplateState.accountDisplayName,
                settlementCurrency: value,
                creditorAgentName: addWireTemplateState.creditorAgentName
            });
        }

        setAddWireTemplateState(newAddWireTemplateState);
        updateFormError(name)
    }

    const handleRemittanceInformationChange = (e) => {
        const value = e.currentTarget.value;
        setAddWireTemplateState({...addWireTemplateState, remittanceInformation: value});

        const remittanceError = validateRemittanceInformation(value);
        setFormErrors(previousErrors => ({...previousErrors, remittanceInformation: remittanceError}));
    }

    const handleChangeAutocomplete = (e) => {
        const selectedEntityValue = e?.value || '';
        const selectedEntityLabel = e?.label || '';

        setManagerDetails(selectedEntityValue, selectedEntityLabel,true);
        setAddWireTemplateState({
            ...addWireTemplateState,
            managerId: selectedEntityValue,
            managerName: selectedEntityLabel,
            accountId: '',
            accountCode: '',
            accountName: '',
            accountDisplayName: '',
            settlementCurrency: DEFAULT_SETTLEMENT_CURRENCY,
            templateName: generateDefaultWireTemplateName({
                accountDisplayName: '',
                settlementCurrency: '',
                creditorAgentName: addWireTemplateState.creditorAgentName
            })
        });
        updateFormError('entity')
    }

    const renderCurrencyOption = (currency) => {
        const currencyCode = currency.currencyCode || currency.isoCurrencyCode;

        if (!currencyCode) {
            return null;
        }

        const optionLabel = currency.currencyName ? `${currencyCode} - ${currency.currencyName}` : currencyCode;

        return <option key={currencyCode} value={currencyCode}>{optionLabel}</option>;
    }

    const handleInputChangeAutoComplete = (enteredValue) => {
        updateFormError('entity')

        const newData = entityAccountsOptionsNoSwap.filter(e => {
                return e.shortName.toLowerCase().includes((enteredValue || '').toLowerCase()) || e.longName.toLowerCase().includes((enteredValue || '').toLowerCase())
            }
        )

        setLocalEntityAccountsOptions(newData);
    }

    const handleAccountChange = (e) => {
        const accountId = e.target.value;
        const accountToSelect = accountsOptionsNoSwap.find(account => account.accountId.toString() === accountId);

        if (accountToSelect) {
            const accountDisplayName = `${accountToSelect.accountCode}-${accountToSelect.name}`;
            setAccountDetails(accountToSelect.accountId, accountToSelect.name, accountToSelect.accountCode,true);
            setAddWireTemplateState({
                ...addWireTemplateState,
                accountId: accountId,
                accountCode: accountToSelect.accountCode,
                accountName: accountToSelect.nameShort,
                accountDisplayName: accountDisplayName,
                templateName: generateDefaultWireTemplateName({
                    accountDisplayName: accountDisplayName,
                    settlementCurrency: addWireTemplateState.settlementCurrency,
                    creditorAgentName: addWireTemplateState.creditorAgentName
                })
            });
        } else {
            setAccountDetails('', '', null);
            setAddWireTemplateState({
                ...addWireTemplateState,
                accountId: '',
                accountCode: '',
                accountName: '',
                accountDisplayName: '',
                templateName: generateDefaultWireTemplateName({
                    accountDisplayName: '',
                    settlementCurrency: addWireTemplateState.settlementCurrency,
                    creditorAgentName: addWireTemplateState.creditorAgentName
                })
            });
        }
        updateFormError('account')
    }

    const handleClear = (e) => {
        e.preventDefault();
        clearAddNewWireTemplateState();
        clearEntityAccounts(undefined,undefined,true);
        setFormErrors({});
    }

    const handleNext = (e) => {
        e.preventDefault();

        const errors = validateStep1(addWireTemplateState);
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            return
        }
        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=nextStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + nextStepPage);
    }

    return (
        <WireTemplateWizardLayout currentStep={1} stepTitle="Template Information">
            <ShiftEnterPopupMessage message="Shift + Enter can be used for a new line only when your cursor is in the Remittance Information text area." />
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group controlId="formWireTemplateStep1Entity" id={(!!formErrors.entity ? 'td-select-error' : '')}>
                            <Form.Label column={false} className="filter-input-label">Entity <span className='red-cell'>*</span></Form.Label>
                            <CustomSelect
                                isDisabled={false}
                                placeholder={'Select'}
                                options={options}
                                value={Object.keys(selectedOption).length > 0 ? selectedOption : null}
                                onChange={handleChangeAutocomplete}
                                onInputChange={handleInputChangeAutoComplete}
                                isValid={!formErrors.managerId}
                            />
                            {!!formErrors.managerId &&
                            <Form.Control.Feedback type={'invalid'} style={{display: 'block'}}>
                                <b><i className="bi bi-exclamation-circle"></i>{formErrors.managerId}</b>
                            </Form.Control.Feedback>
                            }
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="formWireTemplateStep1Account" className={'td-select'} id={(!!formErrors.accountId ? 'td-select-error' : '')}>
                            <Form.Label column={false} className="filter-input-label">Account <span className='red-cell'>*</span></Form.Label>
                            <Form.Control as="select" name='accountId' value={addWireTemplateState.accountId || ''} onChange={handleAccountChange}
                                          isInvalid={!!formErrors.accountId}>
                                <option value={''}>Select</option>
                                {accountsOptionsNoSwap &&
                                    accountsOptionsNoSwap.map((account) => <option key={account.accountId} value={account.accountId}>{account.accountCode}-{account.name}</option>)}
                            </Form.Control>
                            <Form.Control.Feedback type={'invalid'}>
                                <b><i className="bi bi-exclamation-circle"></i>{formErrors.accountId}</b>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <Form.Group controlId="formWireTemplateStep1Currency" className={'td-select'} id={(!!formErrors.settlementCurrency ? 'td-select-error' : '')}>
                            <Form.Label className="filter-input-label">Settlement Currency <span className='red-cell'>*</span> </Form.Label>
                            <Form.Control as="select" name='settlementCurrency' value={addWireTemplateState.settlementCurrency} onChange={handleChange}
                                          isInvalid={!!formErrors.settlementCurrency}>
                                <option value={''}>Select</option>
                                {currencies &&
                                    currencies.map(renderCurrencyOption)}
                            </Form.Control>
                            <Form.Control.Feedback type={'invalid'}>
                                <b><i className="bi bi-exclamation-circle"></i>{formErrors.settlementCurrency}</b>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="formWireTemplateStep1PurposeCode">
                            <Form.Label column={false} className="filter-input-label">Sender to Receiver Info</Form.Label>
                            <Form.Control name='purposeCode' value={addWireTemplateState.purposeCode || ''} onChange={handleChange}
                                          isInvalid={!!formErrors.purposeCode} />
                            <Form.Control.Feedback type={'invalid'}>
                                <b><i className="bi bi-exclamation-circle"></i>{formErrors.purposeCode}</b>
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="formWireTemplateStep1SenderBIC">
                            <Form.Label column={false} className="filter-input-label">Sender Bank BIC</Form.Label>
                            <Form.Control name='senderBIC' value={addWireTemplateState.senderBIC || ''} onChange={handleChange} disabled={true}/>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <Form.Group controlId="formWireTemplateStep1RemittanceInformation">
                            <Form.Label column={false} className="filter-input-label">Remittance Information</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                name='remittanceInformation'
                                placeholder="Leave a comment"
                                maxLength={143}
                                value={addWireTemplateState.remittanceInformation || ''}
                                onChange={handleRemittanceInformationChange}
                                isInvalid={!!formErrors.remittanceInformation}
                            />
                            <Form.Control.Feedback type={'invalid'}>
                                <b><i className="bi bi-exclamation-circle"></i>{formErrors.remittanceInformation}</b>
                            </Form.Control.Feedback>
                            <div className="awt-helper">
                                <span>Optional(Max 4 lines of data,max 35 characters per line)</span>
                                <span className="awt-helper__count">{(addWireTemplateState.remittanceInformation || '').length} / 143</span>
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <div className="awt-actions">
                    <button className="btn td-btn-secondary-clear" onClick={handleClear}>Clear</button>
                    <button className="btn td-btn-primary-light" onClick={handleNext}>Next</button>
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}

